import { ACCESS_TOKEN } from '../assets/constants'
import { ensureValidAccessToken } from '../services/auth/tokenRefresh'

type Phase = 'auth' | 'profile' | 'chart' | 'open'

const PHASE_RANK: Record<Phase, number> = {
	auth: 0,
	profile: 1,
	chart: 2,
	open: 3
}

/** Routes where chart candle load should finish before secondary APIs. */
function isChartPriorityRoute() {
	const path = window.location.pathname
	return (
		/\/chart(\/|$)/.test(path) ||
		path.includes('/embedded-chart') ||
		path.includes('/embedded-strategyChart') ||
		path.includes('/multi-strike-chart') ||
		path.includes('/embedded-multistrike')
	)
}

let phase: Phase = 'auth'
const listeners = new Set<() => void>()
let openTimer: ReturnType<typeof setTimeout> | null = null

function notify() {
	listeners.forEach((listener) => listener())
}

function setPhase(next: Phase) {
	if (PHASE_RANK[next] <= PHASE_RANK[phase]) return
	phase = next
	notify()
}

export function isAuthRoute() {
	const path = window.location.pathname
	return path.includes('/login') || path.includes('/auth-status') || path.includes('/signup')
}

function isChartRequest(url: string) {
	if (url.includes('/v2/security/all')) return false
	return (
		url.includes('/user-dashboards') ||
		url.includes('/supercharts/') ||
		url.includes('/v2/security') ||
		// Both candle endpoints and indicator scripts: the volume pane needs its script and
		// (for some indicators) a follow-up /candles call right after the primary candles.
		url.includes('/candles') ||
		url.includes('/indicators/script') ||
		url.includes('/user-security-expression')
	)
}

function requiredPhase(url: string): Phase {
	if (url.includes('/v2/security/all')) return 'profile'
	if (url.includes('/user/me')) return 'profile'
	if (isChartRequest(url)) return 'chart'
	// On chart pages, hold secondary APIs until candles start (or timeout).
	// Everywhere else, allow page APIs as soon as /user/me has completed.
	if (isChartPriorityRoute()) return 'open'
	return 'chart'
}

function canProceed(url: string) {
	if (!localStorage.getItem(ACCESS_TOKEN) || isAuthRoute()) return true
	return PHASE_RANK[phase] >= PHASE_RANK[requiredPhase(url)]
}

export function waitForInitialLoadSlot(url: string): Promise<void> {
	if (canProceed(url)) return Promise.resolve()
	return new Promise((resolve) => {
		const check = () => {
			if (!canProceed(url)) return
			listeners.delete(check)
			resolve()
		}
		listeners.add(check)
	})
}

export function markProfileLoaded() {
	setPhase('chart')
	if (openTimer) clearTimeout(openTimer)
	if (!isChartPriorityRoute()) {
		setPhase('open')
		return
	}
	openTimer = setTimeout(() => setPhase('open'), 8000)
}

/**
 * Called when the chart's first candles arrive. On chart routes the secondary requests (alerts,
 * calendar, features, notifications, chat…) are released on the next idle moment rather than
 * immediately, so their responses are not parsed while the chart paints its first frame.
 * The 1 s cap keeps them from waiting behind a busy main thread for long.
 */
export function markChartDataRequested() {
	if (!isChartPriorityRoute() || PHASE_RANK[phase] >= PHASE_RANK.open) {
		setPhase('open')
		return
	}
	const release = () => setPhase('open')
	if (typeof window.requestIdleCallback === 'function') {
		window.requestIdleCallback(release, { timeout: 1000 })
	} else {
		setTimeout(release, 250)
	}
}

void ensureValidAccessToken().finally(() => {
	setPhase('profile')
	if (!openTimer) {
		openTimer = setTimeout(() => setPhase('open'), 8000)
	}
})
